import React, { useState, useEffect, useCallback } from "react";
import HeaderContainer from "./Header";
import { useNavigate } from "react-router-dom";
import { env, useGlobalState } from "../helpers/GlobalStateProvider"; // Ensure this hook is set up similarly to sign in page
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { getApiUrl } from "../config";
import { useModalState } from "../helpers/ModalProvider";
import { useDisconnect, useWalletClient } from "wagmi";
import { migrateLocalChatHistory } from "./chatbot/chatHistoryStorage";
import { getToken, setToken } from "../helpers/tokenManager";
import { Box, Card, Flex, Heading, Input, Link, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";
import { Checkbox } from "../components/ui/checkbox";
import { isValidEmail } from "../helpers/validation";

const CreateAccount: React.FC<any> = () => {
  const { registerWallet } = useGlobalState();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [waitingForWallet, setWaitingForWallet] = useState(false); // New state
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const { openConnectModal } = useConnectModal();

  const { showErrorMessage } = useModalState();
  const { disconnect } = useDisconnect();
  const { refreshAccountInfo, setBearerToken } = useGlobalState();

  const { data: walletClient } = useWalletClient();

  const handleCreateEmailAccount = async () => {
    // Normalize once and register the normalized value. isValidEmail trims, so
    // validating the raw string and submitting the padded one would register
    // an address with surrounding whitespace.
    const trimmedEmail = email.trim();

    // Check if the email is valid
    if (!isValidEmail(trimmedEmail)) {
      showErrorMessage("Please enter a valid email address.");
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      showErrorMessage("Passwords do not match.");
      return;
    }

    // Check if terms are agreed to
    if (!agreedToTerms) {
      showErrorMessage("Please agree to the terms of service and privacy policy.");
      return;
    }

    try {
      const response = await fetch(`${getApiUrl(env)}/v3/apiKey/email_register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData.message || "Error creating account.";
        showErrorMessage(message);
        return;
      }

      const data = await response.json();
      setToken(data.bearerToken);
      setBearerToken(data.bearerToken);
      migrateLocalChatHistory();

      // Record TOS agreement
      try {
        await fetch(`${getApiUrl(env)}/v3/apiKey/agree_tos`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${data.bearerToken}`,
          },
        });
      } catch (tosError) {
        if (process.env.NODE_ENV === 'development') console.error("Error recording TOS agreement:", tosError);
        // Don't fail the signup if TOS recording fails.
      }

      navigate("/confirm_email");
      refreshAccountInfo();
    } catch (error) {
      showErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  const runWalletRegister = useCallback(async () => {
    setWaitingForWallet(false); // Reset the state now that we're trying to register

    // Check if terms are agreed to
    if (!agreedToTerms) {
      showErrorMessage("Please agree to the terms of service and privacy policy.");
      return;
    }

    try {
      await registerWallet();

      // Record TOS agreement after successful wallet registration
      const bearerToken = getToken();
      if (bearerToken) {
        try {
          await fetch(`${getApiUrl(env)}/v3/apiKey/agree_tos`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${bearerToken}`,
            },
          });
        } catch (tosError) {
          if (process.env.NODE_ENV === 'development') console.error("Error recording TOS agreement:", tosError);
          // Don't fail the registration if TOS recording fails
        }
        navigate("/dashboard");
      }
    } catch (e) {
      disconnect();
      showErrorMessage(
        "Current Wallet already registered. You must register with a different wallet",
      );
    }
  }, [agreedToTerms, registerWallet, disconnect, showErrorMessage, navigate]);

  useEffect(() => {
    // Check that wallet is not null or undefined
    if (waitingForWallet && walletClient) {
      runWalletRegister();
    }
  }, [waitingForWallet, walletClient, runWalletRegister]);

  const handleWalletButtonClick = async () => {
    // If no wallet connected, open the modal and wait for the user to connect
    if (!walletClient) {
      openConnectModal?.();
      setWaitingForWallet(true);
    } else {
      // If already connected, just run the wallet registration
      runWalletRegister();
    }
  };

  useEffect(() => {
      disconnect();
  }, [disconnect])

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      maxWidth="1392px"
      margin="0 auto"
      paddingTop="24px"
      paddingBottom="24px"
      direction="column"
      gap="24px"
    >
      {/* Header */}
      <HeaderContainer behavior="signin" />

      {/* Main container to center the content */}
      <Flex flex={1} justifyContent="center" alignItems="center" padding="20px">
        {/* Content wrapper for max width */}
        <Stack maxWidth="480px" width="100%" alignItems="center" gap={8}>
          {/* Page title */}
          <Heading
            as="h1"
            textAlign="center"
            color="#18181b"
            fontSize="24px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            Create an account
          </Heading>

          {/* Create Account Card */}
          <Card.Root width="100%">
            <Card.Body>
              <Stack gap={6}>
                <Stack gap={4}>
                  {/* Email field */}
                  <Field label="Email address">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fontSize="sm"
                    />
                  </Field>

                  {/* Password field */}
                  <Field label="Password">
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fontSize="sm"
                    />
                  </Field>

                  {/* Confirm Password field */}
                  <Field label="Confirm password">
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      fontSize="sm"
                    />
                  </Field>
                </Stack>

                {/* Terms Agreement Section */}
                <Checkbox
                  colorPalette="purple"
                  alignItems="flex-start"
                  checked={agreedToTerms}
                  onCheckedChange={(e) => setAgreedToTerms(e.checked === true)}
                >
                  <Text fontSize="sm" color="#52525b" lineHeight="20px" fontWeight="normal">
                    I have read and agree to the CoinCap{" "}
                    <Link
                      href="https://coincap.io/terms-of-service?tab=terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="#9333ea"
                      textDecoration="underline"
                    >
                      terms of service
                    </Link>
                    {" "}and{" "}
                    <Link
                      href="https://coincap.io/terms-of-service?tab=privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="#9333ea"
                      textDecoration="underline"
                    >
                      privacy policy
                    </Link>
                    .
                  </Text>
                </Checkbox>

                {/* Create Account Button */}
                {/* Intentionally not using the `disabled` prop: clicking while the
                    terms are unchecked must still trigger the validation error
                    message, matching the original behavior. */}
                <Button
                  onClick={handleCreateEmailAccount}
                  variant="solid"
                  colorPalette="purple"
                  bg={agreedToTerms ? "#9333ea" : "#d4d4d8"}
                  width="100%"
                  cursor={agreedToTerms ? "pointer" : "not-allowed"}
                >
                  Create account
                </Button>

                {/* Connect a Wallet Section */}
                <Stack gap={6}>
                  <Flex width="100%" alignItems="center" gap={4}>
                    <Box flex={1} height="1px" bg="#e4e4e7" />
                    <Text fontSize="sm" color="#a1a1aa">
                      Or connect a wallet
                    </Text>
                    <Box flex={1} height="1px" bg="#e4e4e7" />
                  </Flex>
                  <Button
                    onClick={handleWalletButtonClick}
                    variant="outline"
                    colorPalette={agreedToTerms ? "purple" : "gray"}
                    width="100%"
                    opacity={agreedToTerms ? 1 : 0.6}
                    cursor={agreedToTerms ? "pointer" : "not-allowed"}
                  >
                    Connect a wallet
                  </Button>
                </Stack>
              </Stack>
            </Card.Body>
          </Card.Root>

          {/* Bottom link */}
          <Text textAlign="center" fontSize="sm" color="#a1a1aa">
            Already have an account?{" "}
            <Link
              href="/signin"
              rel="noopener noreferrer"
              color="#9333ea"
              fontWeight="semibold"
            >
              Sign in here
            </Link>
          </Text>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default CreateAccount;
